import React, {useCallback, useEffect, useState} from "react";
import QTextBox from "../../../../common/components/QTextBox";
import QSelect from "../../../../common/components/QSelect";
import SetupApi from "../../../../common/api/functions/Setup";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {
    CustomerGroupInterface,
    LayoutInterface,
    PaymentTermInterface,
    ProductGroupInterface,
    ProductInterface,
    VatZoneInterface,
} from "../../../../interfaces/economic/CommonInterface";
import {SelectOptionInterface} from "../../../../interfaces/SelectOptionInterface";
import {useDispatch} from "react-redux";
import {setLoading} from "../../../../app/rootReducer";
import QSelectSearch from "../../../../common/components/QSelectSearch";
import {debounce} from "lodash"
import {useTranslation} from "react-i18next";
import {Input, Label} from "reactstrap";
import SetupOptionsError from "./SetupOptionsError";
import InvoiceDefaultsGroup from "./InvoiceDefaultsGroup";
import {applyInvoiceSetupDefaults, setInvoiceDefaultOptionsCache} from "../helpers/invoiceDefaults";
import {
    applyCommonSetupDefaults,
    COMMON_DEFAULT_KEYS,
    setCommonDefaultOptionsCache,
} from "../helpers/commonDefaults";

interface Props {
    formik: any
}

const Invoice: React.FC<Props> = ({formik}) => {
    const {t} = useTranslation();
    const [layouts, setLayouts] = useState<LayoutInterface[]>([]);
    const [paymentTerms, setPaymentTerms] = useState<PaymentTermInterface[]>([]);
    const [customerGroups, setCustomerGroups] = useState<CustomerGroupInterface[]>([]);
    const [customerPaymentTerms, setCustomerPaymentTerms] = useState<PaymentTermInterface[]>([]);
    const [productGroups, setProductGroups] = useState<ProductGroupInterface[]>([]);
    const [bookedWiths, setBookedWiths] = useState({});
    const [otherRef, setOtherRef] = useState({});
    const [invoiceSources, setInvoiceSources] = useState({
        invoice: 'Stripe Invoice',
        checkout: 'Stripe Checkout',
    });
    const [vatZones, setVatZones] = useState<VatZoneInterface[]>([]);
    const [loadError, setLoadError] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [showMore, setShowMore] = useState(false);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
        vat: true,
        document: false,
        booking: false,
        common: false,
    });
    const dispatch = useDispatch();

    const toggleGroup = (key: string) => {
        setOpenGroups((prev) => ({...prev, [key]: !prev[key]}));
    };

    const seedDefaults = useCallback((
        currentValues: Record<string, any>,
        optionData?: {
            layouts?: LayoutInterface[];
            paymentTerms?: PaymentTermInterface[];
            customerGroups?: CustomerGroupInterface[];
            customerPaymentTerms?: PaymentTermInterface[];
            productGroups?: ProductGroupInterface[];
        },
    ) => {
        let next = applyInvoiceSetupDefaults(currentValues, {
            layouts: optionData?.layouts,
            paymentTerms: optionData?.paymentTerms,
        });
        next = applyCommonSetupDefaults(next, {
            customerGroups: optionData?.customerGroups,
            paymentTerms: optionData?.customerPaymentTerms,
            productGroups: optionData?.productGroups,
        });

        const keys = [
            'economic_invoice_booked_with',
            'economic_invoice_send_out',
            'economic_invoice_other_ref',
            'economic_invoice_vat_zone_domestic_number',
            'economic_invoice_vat_zone_eu_number',
            'economic_invoice_vat_zone_abroad_number',
            'economic_invoice_vat_zone_domestic_without_vat_number',
            'economic_invoice_layout_number',
            'economic_invoice_payment_terms_number',
            ...COMMON_DEFAULT_KEYS,
        ] as const;

        keys.forEach((key) => {
            if (next[key] !== currentValues[key]) {
                formik.setFieldValue(key, next[key]);
            }
        });
    }, [formik]);

    useEffect(() => {
        seedDefaults(formik.values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        setOptionsLoading(true);
        setLoadError(false);

        Promise.all([
            SetupApi.getDataSetupInvoice(),
            SetupApi.getDataSetupCommon(),
        ]).then(([invoiceRes, commonRes]: any[]) => {
            if (cancelled) return;
            const data = invoiceRes?.data?.data || {};
            const common = commonRes?.data?.data || {};
            const nextLayouts = data.layouts || [];
            const nextPaymentTerms = data.paymentTerms || [];
            const nextCustomerGroups = common.customerGroups || [];
            const nextCustomerPaymentTerms = common.paymentTerms || [];
            const nextProductGroups = common.productGroups || [];

            setLayouts(nextLayouts);
            setPaymentTerms(nextPaymentTerms);
            setCustomerGroups(nextCustomerGroups);
            setCustomerPaymentTerms(nextCustomerPaymentTerms);
            setProductGroups(nextProductGroups);
            setBookedWiths(data.bookedWiths || {});
            setOtherRef(data.otherRef || {});
            if (data.invoiceSources) {
                setInvoiceSources(data.invoiceSources);
            }
            setVatZones(data.vatZones || []);

            setInvoiceDefaultOptionsCache({
                layouts: nextLayouts,
                paymentTerms: nextPaymentTerms,
            });
            setCommonDefaultOptionsCache({
                customerGroups: nextCustomerGroups,
                paymentTerms: nextCustomerPaymentTerms,
                productGroups: nextProductGroups,
            });
            seedDefaults(formik.values, {
                layouts: nextLayouts,
                paymentTerms: nextPaymentTerms,
                customerGroups: nextCustomerGroups,
                customerPaymentTerms: nextCustomerPaymentTerms,
                productGroups: nextProductGroups,
            });
            dispatch(setLoading(false));
        }).catch(() => {
            if (!cancelled) {
                setLoadError(true);
                dispatch(setLoading(false));
            }
        }).finally(() => {
            if (!cancelled) setOptionsLoading(false);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, reloadKey]);

    const promiseOptions = useCallback(debounce((e: string, callback) => {
        const controller = new AbortController();
        if (e.length > 1) {
            SetupApi.searchProduct(e).then(async (response: ResponseInterface | any) => {
                return response?.data?.data?.map((item: ProductInterface) => ({
                    label: item.productNumber + ' - ' + item.name,
                    value: item.productNumber,
                }))
            }).then(results => {
                callback(results);
            });
        } else {
            callback(null);
        }

        return () => controller.abort();
    }, 500), []);

    return (
        <React.Fragment>
            {loadError ? (
                <SetupOptionsError
                    loading={optionsLoading}
                    onRetry={() => setReloadKey((k) => k + 1)}
                />
            ) : null}

            <section className="mb-4">
                <h6 className="mb-1">{t("common.setup.invoiceSectionSource")}</h6>
                <p className="text-muted small mb-3">{t("common.setup.invoiceSectionSourceHelp")}</p>
                <QSelect
                    label={t("economic.invoiceSource")}
                    value={formik.values.stripe_invoice_source}
                    onChange={(option: SelectOptionInterface) => {
                        formik.setFieldValue('stripe_invoice_source', option.value);
                    }}
                    options={Object.entries(invoiceSources).map(([key, value]) => ({
                        label: value,
                        value: key,
                    }))}
                    error={formik.errors?.stripe_invoice_source}
                />
                <div className="alert alert-light border mb-0">
                    <div className="mb-2">
                        <Label className="d-block mb-1">
                            {t("economic.checkoutPricesIncludeVat")}
                            <Input
                                style={{marginLeft: '10px'}}
                                type="checkbox"
                                className={'form-control-zp'}
                                checked={!!formik.values.stripe_checkout_prices_include_vat}
                                onChange={(e) => {
                                    formik.setFieldValue(
                                        'stripe_checkout_prices_include_vat',
                                        e.target.checked
                                    );
                                }}
                            />
                        </Label>
                        <small className="text-muted d-block">
                            {t("economic.checkoutPricesIncludeVatHelp")}
                        </small>
                    </div>
                    {formik.values.stripe_checkout_prices_include_vat && (
                        <>
                            <div className="alert alert-warning py-2 mb-2" role="status">
                                {t("economic.checkoutVatHint")}
                            </div>
                            <QTextBox
                                label={t("economic.checkoutIncludedVatRate")}
                                value={formik.values.stripe_checkout_included_vat_rate}
                                onChange={(event: any) => {
                                    formik.setFieldValue('stripe_checkout_included_vat_rate', event);
                                }}
                                error={formik.errors?.stripe_checkout_included_vat_rate}
                            />
                        </>
                    )}
                </div>
            </section>

            <section className="pt-3 border-top">
                <button
                    type="button"
                    className="btn btn-link btn-sm px-0 cl-orange-zp text-decoration-none"
                    onClick={() => setShowMore((open) => !open)}
                >
                    {showMore
                        ? t("common.setup.invoiceAdvancedHide")
                        : t("common.setup.invoiceAdvanced")}
                </button>
                <p className="text-muted small mb-0">{t("common.setup.invoiceAdvancedHelp")}</p>

                {showMore ? (
                    <div className="mt-3">
                        <InvoiceDefaultsGroup
                            title={t("common.setup.invoiceSectionVat")}
                            help={t("common.setup.invoiceVatHelp")}
                            open={openGroups.vat}
                            onToggle={() => toggleGroup('vat')}
                        >
                            {[
                                ['economic_invoice_vat_zone_domestic_number', 'economic.vatZoneDomestic'],
                                ['economic_invoice_vat_zone_eu_number', 'economic.vatZoneEu'],
                                ['economic_invoice_vat_zone_abroad_number', 'economic.vatZoneAbroad'],
                                ['economic_invoice_vat_zone_domestic_without_vat_number', 'economic.vatZoneDomesticWithoutVat'],
                            ].map(([field, label]) => (
                                <QSelect
                                    key={field}
                                    menuPortal
                                    label={t(label)}
                                    value={formik.values[field]}
                                    onChange={(option: SelectOptionInterface) => {
                                        formik.setFieldValue(field, option.value);
                                    }}
                                    options={vatZones.map((zone: VatZoneInterface) => ({
                                        label: zone.name,
                                        value: zone.vatZoneNumber,
                                    }))}
                                    error={formik.errors?.[field]}
                                />
                            ))}
                        </InvoiceDefaultsGroup>

                        <InvoiceDefaultsGroup
                            title={t("common.setup.invoiceSectionDocument")}
                            help={t("common.setup.invoiceSectionDocumentHelp")}
                            open={openGroups.document}
                            onToggle={() => toggleGroup('document')}
                        >
                            <QSelect
                                menuPortal
                                label={t("economic.layout")}
                                value={formik.values.economic_invoice_layout_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_invoice_layout_number', option.value);
                                }}
                                options={layouts.map((a: LayoutInterface) => ({
                                    label: a.name,
                                    value: a.layoutNumber
                                }))}
                                error={formik.errors?.economic_invoice_layout_number}
                            />
                            <QSelect
                                menuPortal
                                label={t("economic.paymentTerms")}
                                value={formik.values.economic_invoice_payment_terms_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_invoice_payment_terms_number', option.value);
                                }}
                                options={paymentTerms.map((a: PaymentTermInterface) => ({
                                    label: a.name,
                                    value: a.paymentTermsNumber
                                }))}
                                error={formik.errors?.economic_invoice_payment_terms_number}
                            />
                        </InvoiceDefaultsGroup>

                        <InvoiceDefaultsGroup
                            title={t("common.setup.invoiceSectionBooking")}
                            help={t("common.setup.invoiceSectionBookingHelp")}
                            open={openGroups.booking}
                            onToggle={() => toggleGroup('booking')}
                        >
                            <QSelect
                                menuPortal
                                label={t("economic.bookedWith")}
                                value={formik.values.economic_invoice_booked_with}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_invoice_booked_with', option.value);
                                }}
                                options={Object.entries(bookedWiths).map(([key, value]) => ({
                                    label: value as string,
                                    value: key
                                }))}
                                error={formik.errors?.economic_invoice_booked_with}
                            />
                            {String(formik.values.economic_invoice_booked_with) === '2' && (
                                <div className="mb-3">
                                    <Label className="d-block mb-1">
                                        {t("economic.sendOutInvoice")}
                                        <Input
                                            style={{marginLeft: '10px'}}
                                            type="checkbox"
                                            checked={!!formik.values.economic_invoice_send_out}
                                            onChange={(e) => {
                                                formik.setFieldValue('economic_invoice_send_out', e.target.checked);
                                            }}
                                        />
                                    </Label>
                                    <p className="text-muted small mb-0">
                                        {t("economic.sendOutInvoiceHelp")}
                                    </p>
                                </div>
                            )}
                            <QSelect
                                menuPortal
                                label={t("economic.otherRef")}
                                value={formik.values.economic_invoice_other_ref}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_invoice_other_ref', option.value);
                                }}
                                options={Object.entries(otherRef).map(([key, value]) => ({
                                    label: value as string,
                                    value: key
                                }))}
                                error={formik.errors?.economic_invoice_other_ref}
                            />
                            <QSelectSearch
                                menuPortal
                                label={t("economic.productDiscount")}
                                value={formik.values.economic_invoice_product_discount_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_invoice_product_discount_number', option.value);
                                }}
                                error={formik.errors?.economic_invoice_product_discount_number}
                                placeholder={t("economic.productDiscountPlaceholder")}
                                promiseOptions={promiseOptions}
                            />
                        </InvoiceDefaultsGroup>

                        <InvoiceDefaultsGroup
                            title={t("common.setup.invoiceSectionCommon")}
                            help={t("common.setup.invoiceCommonHelp")}
                            open={openGroups.common}
                            onToggle={() => toggleGroup('common')}
                        >
                            <QSelect
                                menuPortal
                                label={t("economic.customerGroup")}
                                value={formik.values.economic_customer_group_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_customer_group_number', option.value);
                                }}
                                options={customerGroups.map((a: CustomerGroupInterface) => ({
                                    label: a.name,
                                    value: a.customerGroupNumber
                                }))}
                                error={formik.errors?.economic_customer_group_number}
                            />
                            <QSelect
                                menuPortal
                                label={t("economic.customerPaymentTerms")}
                                value={formik.values.economic_customer_payment_terms_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_customer_payment_terms_number', option.value);
                                }}
                                options={customerPaymentTerms.map((a: PaymentTermInterface) => ({
                                    label: a.name,
                                    value: a.paymentTermsNumber
                                }))}
                                error={formik.errors?.economic_customer_payment_terms_number}
                            />
                            <QSelect
                                menuPortal
                                label={t("economic.productGroup")}
                                value={formik.values.economic_product_group_number}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue('economic_product_group_number', option.value);
                                }}
                                options={productGroups.map((a: ProductGroupInterface) => ({
                                    label: a.name,
                                    value: a.productGroupNumber
                                }))}
                                error={formik.errors?.economic_product_group_number}
                            />
                        </InvoiceDefaultsGroup>
                    </div>
                ) : null}
            </section>
        </React.Fragment>
    )
}

export default Invoice;
